import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChangePasswordModal from './ChangePasswordModal';
import { authApi } from '../services/authApi';
import { useWorkSites } from '../hooks/useWorkSites';
import type { LinkTarget } from '../services/linkedAppsApi';
import { openWorkSite } from '../utils/workSites';
import { useAlert } from '../contexts/AlertContext';
import { deckApi, DECK_OPEN } from '../services/deckApi';
import { MGMT_OPEN } from '../services/dashboardApi';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user, logout, userRole, accessiblePaths } = useAuth();
  // 「경영현황」 · 「문서 · PPT」 — 볼 수 있는가 · 보일 사이트는 백엔드가 정한다(바로 이동과 같은 역할 규칙)
  const workSites = useWorkSites();
  const { showToast } = useAlert();
  // 템플릿 작성 — 서버 설정(grk-deck 키 · 연결)이 안 됐으면 메뉴를 회색 「준비중」으로
  const [deckReady, setDeckReady] = useState(false);
  useEffect(() => {
    if (!DECK_OPEN || !workSites.allowed) return;
    deckApi.status().then((r) => setDeckReady(r.ready)).catch(() => setDeckReady(false));
  }, [workSites.allowed]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    hrManagement: true,
    performanceManagement: true,
    businessResource: true,
    systemManagement: true
  });
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const profilePopupRef = useRef<HTMLDivElement>(null);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleProfilePopup = () => {
    setIsProfilePopupOpen(!isProfilePopupOpen);
  };

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authApi.changePassword(currentPassword, newPassword);
    } catch (error: any) {
      throw error;
    }
  };

  // 클릭 외부 감지하여 팝업 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profilePopupRef.current && !profilePopupRef.current.contains(event.target as Node)) {
        setIsProfilePopupOpen(false);
      }
    };

    if (isProfilePopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfilePopupOpen]);

  // 개인 영역 메뉴 정의
  const getPersonalMenuItems = () => {
    const personalItems = [
      {
        path: '/schedule',
        title: '내 일정',
        roles: ['EMPLOYEE', 'DEVELOPER', 'ADMIN', 'PROJECT_MANAGER'],
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )
      },
      {
        path: '/attendance',
        title: '내 근태',
        roles: ['EMPLOYEE', 'DEVELOPER', 'ADMIN', 'PROJECT_MANAGER'],
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      {
        path: '/evaluation-personal',
        title: '내 평가',
        roles: ['EMPLOYEE', 'DEVELOPER', 'ADMIN', 'PROJECT_MANAGER'],
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        )
      },
      // 경영현황(GRK_admin#115 전체 현황) — 회사 대시보드. 보이는 기준은 백엔드(GET /api/sso/sites) — 관리자 · 개발자
      {
        path: MGMT_OPEN ? '/dashboard' : '/work-sites',
        title: '경영현황',
        roles: [] as string[],
        workSite: true,
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10-3a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1v-7z" />
          </svg>
        )
      },
      // 템플릿 작성(GRK_admin#113) — 원고 → PPTX. 본체는 서버 공용 grk-deck. 보이는 기준은 페이지와 같다(workSites.allowed)
      {
        path: '/ppt-draft',
        title: '템플릿 작성',
        roles: [] as string[],
        deck: true,
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        )
      },
      {
        path: '/corporate-card-upload',
        title: '법인카드 사용내역',
        roles: ['EMPLOYEE', 'DEVELOPER', 'ADMIN', 'PROJECT_MANAGER'],
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        )
      }
    ];

    if (!userRole) return [];
    // 경영현황 · 템플릿 작성은 page_permissions 에 없다 — 백엔드 판단(workSites)만 따른다
    // accessiblePaths가 있으면 그것을 사용, 없으면 기존 역할 기반 필터링
    return personalItems
      .filter((item: { path: string; roles: string[]; workSite?: boolean; deck?: boolean }) => {
        if (item.workSite || item.deck) return workSites.allowed;
        return accessiblePaths.length > 0 ? accessiblePaths.includes(item.path) : item.roles.includes(userRole);
      })
      // 경영현황 — MGMT_OPEN 이 false(운영)면 「준비중」(요구 4, #97). 템플릿 작성 — DECK_OPEN 이 false 거나 grk-deck 연결 전이면 「준비중」
      .map((item: any) => (item.workSite ? { ...item, pending: !MGMT_OPEN } : item.deck ? { ...item, pending: !deckReady } : item));
  };

  // 관리 영역 메뉴 정의
  const getManagementMenuItems = () => {
    const managementSections = {
      businessResource: {
        title: '경영 관리',
        items: [
          {
            path: '/opex',
            title: 'OPEX',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            )
          },
          {
            path: '/ppe',
            title: 'PPE',
            roles: ['DEVELOPER', 'ADMIN', 'PROJECT_MANAGER'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )
          },
          {
            path: '/cash-flow',
            title: 'Cash Flow',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            )
          },
          {
            path: '/schedule-admin',
            title: 'Calendar',
            roles: ['DEVELOPER', 'ADMIN', 'PROJECT_MANAGER'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )
          }
        ]
      },
      hrManagement: {
        title: '인사 관리',
        items: [
          {
            path: '/employees',
            title: '인력관리',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            )
          },
          {
            path: '/hr-cost',
            title: '인력원가',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )
          },
          {
            path: '/cr',
            title: '인사평가',
            roles: ['DEVELOPER', 'ADMIN', 'PROJECT_MANAGER'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            )
          },
          {
            path: '/attendance-admin',
            title: '근태관리',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            )
          },
          {
            path: '/payroll',
            title: '급여대장',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            )
          },
          {
            path: '/corporate-card',
            title: '법인카드 정산',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            )
          }
        ]
      },
      systemManagement: {
        title: '시스템 관리',
        items: [
          {
            path: '/permission',
            title: '권한 관리',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            )
          },
          {
            path: '/notification-settings',
            title: '알림 설정',
            roles: ['DEVELOPER', 'ADMIN'],
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            )
          }
        ]
      }
    };

    if (!userRole) return {};

    const filteredSections: any = {};
    Object.entries(managementSections).forEach(([key, section]) => {
      const filteredItems = section.items.filter((item: { path: string; roles: string[]; workSite?: boolean; site?: LinkTarget }) => {
        // 업무 사이트 — 백엔드 판단만 따른다. 연결 설정 안 된 사이트는 숨기고, 보일 사이트가 없으면 묶음째 숨긴다
        // site 가 없는 줄(전체 현황)은 연결된 사이트가 하나라도 있으면 보인다
        if (item.workSite) return workSites.allowed && (item.site ? workSites.sites.includes(item.site) : workSites.sites.length > 0);
        // accessiblePaths가 있으면 그것을 사용
        if (accessiblePaths.length > 0) {
          return accessiblePaths.includes(item.path);
        }
        // 없으면 기존 역할 기반 필터링
        if (userRole === 'DEVELOPER') return true;
        return item.roles.includes(userRole);
      });

      if (filteredItems.length > 0) {
        filteredSections[key] = {
          ...section,
          items: filteredItems
        };
      }
    });

    return filteredSections;
  };

  // 메뉴 항목 — 보통은 화면 이동, open 이 있으면 그 사이트를 새 탭으로(바로 이동)
  const MenuLink = ({ item, className, title, children }: { item: any; className: string; title?: string; children: React.ReactNode }) =>
    item.pending ? (
      // 서버 설정 전 — 보이기만 하고 누를 수 없다
      <span
        aria-disabled="true"
        title={`${title ?? item.title} — 준비중`}
        className={`${className.replace(/bg-indigo-50|text-indigo-600|text-slate-500|hover:\S+/g, '')} w-full text-slate-300 cursor-not-allowed select-none`}
      >
        {children}
        {isExpanded && (
          <span className="ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-400">준비중</span>
        )}
      </span>
    ) : item.open ? (
      <button
        type="button"
        title={title}
        className={`${className} w-full text-left`}
        onClick={() =>
          openWorkSite(item.open.target, item.open.next).catch((e) =>
            showToast(`이동하지 못했습니다: ${(e as Error).message}`, 'error')
          )
        }
      >
        {children}
      </button>
    ) : (
      <Link to={item.path} title={title} className={className}>
        {children}
      </Link>
    );

  const personalItems = getPersonalMenuItems();
  const managementSections = getManagementMenuItems();

  return (
    <aside className={`${isExpanded ? 'w-56' : 'w-20'} transition-all duration-300 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col py-4 ${isExpanded ? 'px-3' : 'items-center'} relative`}>
      {/* 토글 버튼 - 사이드바 가장자리에 위치 */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`absolute top-1/2 -translate-y-1/2 ${isExpanded ? '-right-3' : '-right-3'} z-50 p-1 rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-all shadow-md`}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="14" 
          height="14" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>

      {/* 로고 */}
      <div className={`${isExpanded ? 'mb-6 px-3' : 'mb-6'}`}>
        <Link to="/" className={`${isExpanded ? 'flex items-center justify-center' : 'flex items-center justify-center'} group w-full`}>
          <div className={`${isExpanded ? 'w-full h-16' : 'h-14 w-14'} flex items-center justify-center group-hover:scale-105 transition-all duration-200`}>
            <img 
              src="/GRK_partners_세로형.jpg" 
              alt="GRK Partners Logo" 
              className={`${isExpanded ? 'h-14 w-auto max-w-full' : 'h-12 w-12'} object-contain`}
              style={{ mixBlendMode: 'multiply' }}
            />
          </div>
        </Link>
      </div>

      {/* 메뉴 아이템 */}
      <nav className={`flex-1 ${isExpanded ? 'space-y-1' : 'flex flex-col items-center space-y-2'} overflow-y-auto`}>
        {/* 개인 영역 */}
        {personalItems.length > 0 && (
          <div className="w-full">
            {isExpanded && (
              <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                개인 영역
              </div>
            )}
            <div className={`${isExpanded ? 'space-y-1' : 'flex flex-col items-center space-y-2'}`}>
              {personalItems.map((item) => (
                <MenuLink
                  key={item.path}
                  item={item}
                  title={!isExpanded ? item.title : undefined}
                  className={`${
                    isExpanded 
                      ? 'flex items-center gap-3 px-3 py-2' 
                      : 'h-10 w-10 flex items-center justify-center'
                  } rounded-lg transition-colors w-full ${
                    location.pathname === item.path
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {item.icon}
                  </div>
                  {isExpanded && (
                    <span className="text-sm font-medium whitespace-nowrap">
                      {item.title}
                    </span>
                  )}
                </MenuLink>
              ))}
            </div>
          </div>
        )}

        {/* 관리 영역 */}
        {Object.keys(managementSections).length > 0 && (
          <div className="w-full mt-4">
            {isExpanded && (
              <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                관리 영역
              </div>
            )}
            
            {Object.entries(managementSections).map(([sectionKey, section]: [string, any]) => (
              <div key={sectionKey} className="mb-2">
                {isExpanded ? (
                  <>
                    {/* 섹션 헤더 - 확장된 상태 */}
                    <button
                      onClick={() => toggleSection(sectionKey)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-colors"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${expandedSections[sectionKey] ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span>{section.title}</span>
                    </button>
                    
                    {/* 섹션 아이템들 */}
                    {expandedSections[sectionKey] && (
                      <div className="ml-6 space-y-1">
                        {section.items.map((item: any) => (
                          <MenuLink
                            key={item.path}
                            item={item}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              location.pathname === item.path
                                ? 'bg-indigo-50 text-indigo-600'
                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                            }`}
                          >
                            <div className="flex-shrink-0">{item.icon}</div>
                            <span className="text-sm font-medium">{item.title}</span>
                          </MenuLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  /* 축소된 상태 - 섹션별 아이템들을 바로 표시 */
                  <div className="space-y-1">
                    {section.items.map((item: any) => (
                      <MenuLink
                        key={item.path}
                        item={item}
                        title={item.title}
                        className={`h-10 w-10 flex items-center justify-center rounded-lg transition-colors ${
                          location.pathname === item.path
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        }`}
                      >
                        <div className="flex-shrink-0">{item.icon}</div>
                      </MenuLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </nav>
      {/* 프로필 섹션 */}
      <div className={`mt-auto ${isExpanded ? 'w-full' : ''}`} ref={profilePopupRef}>
        <div className={`${isExpanded ? 'flex items-center gap-3 px-3 py-2' : ''}`}>
          <div className="relative">
            <div 
              onClick={toggleProfilePopup}
              className={`h-10 w-10 rounded-full cursor-pointer border-2 transition-colors overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0 ${
                isProfilePopupOpen ? 'border-indigo-300' : 'border-transparent hover:border-indigo-300'
              }`}
            >
            {user?.profilePicture ? (
              <img
                className="h-full w-full object-cover"
                src={user.profilePicture}
                alt="User avatar"
                title={user?.name || 'User'}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <span className="text-gray-500 font-semibold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            )}
            </div>
            {/* 클릭 기반 팝업 */}
            {isProfilePopupOpen && (
              <div className={`absolute ${isExpanded ? 'bottom-12 left-0' : 'bottom-12 left-12'} bg-white shadow-xl rounded-lg py-3 px-4 z-[100] border border-slate-200 w-64`}>
                {/* 화살표 */}
                <div className="absolute -left-2 bottom-4 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[8px] border-r-white"></div>
                <div className="absolute -left-[9px] bottom-4 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[8px] border-r-slate-200"></div>
                <div className="flex items-center space-x-2 mb-2">
                  <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                    {user?.profilePicture ? (
                      <img
                        className="h-full w-full object-cover"
                        src={user.profilePicture}
                        alt="User avatar"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className="text-gray-500 font-semibold text-xs">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="mb-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    user?.role === 'ADMIN'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user?.role === 'ADMIN' ? '관리자' : '일반 사용자'}
                  </span>
                </div>
                <div className="space-y-1 border-t border-slate-200 pt-2">
                  <button
                    onClick={() => {
                      setIsChangePasswordModalOpen(true);
                      setIsProfilePopupOpen(false);
                    }}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    <span>비밀번호 변경</span>
                  </button>
                  <button
                    onClick={() => {
                      logout();
                      setIsProfilePopupOpen(false);
                    }}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>로그아웃</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          {isExpanded && (
            <div 
              onClick={toggleProfilePopup}
              className="flex-1 min-w-0 cursor-pointer"
            >
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {userRole}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 비밀번호 변경 모달 */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onSubmit={handleChangePassword}
      />
    </aside>
  );
};

export default Sidebar;